// Purpose: THE universal evidence window component - single source of truth for ALL evidence displays
// Audience: All evidence displays across the application (tiles, modals, public, private)
// Features:
// - Unified evidence display that adapts based on mode
// - Configurable display modes (tile, modal, compact)
// - Dynamic components based on context and user permissions
// - Consistent action buttons and behavior
// - Media preview with mode-appropriate controls
// - Privacy controls for owners
// - Extensible through attached components

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { FaEdit, FaEllipsisV, FaDownload } from 'react-icons/fa';
import AIAnalysisComponent from '../analysis/AIAnalysisComponent';
import EvidenceErrorBoundary from './EvidenceErrorBoundary';
import { useNotifications } from '../notifications/NotificationsProvider';
import { isVideoFile, isImageFile, isAudioFile } from '../../utils/fileTypeUtils.js';
import { useVideoAutoplay } from '../../hooks/useVideoAutoplay';
import '../../styles/UniversalEvidenceWindow.css';

const UniversalEvidenceWindow = ({
  evidence,
  mode = 'tile',
  // Modal layer management props (new)
  modalId = null,
  zIndex = null,
  useModalLayerManager = false, // 'tile', 'modal', 'compact'
  isOpen = true,
  onClose,
  onOpenModal,
  onUpdateSuccess,
  currentUser,
  casefileId,
  casefileName,
  actionButtons = [],
  attachedComponents = [], // Additional components to attach (e.g., AIAnalysisComponent)
  disableAutoAIAnalysis = false,
  desiredAnalysisTab = 'selected',
  navigationTrigger,
  isSelected = false,
  onSelectionChange,
  formatFileSize,
  className = '',
  // Summary state props (managed by parent)
  evidenceSummary,
  onSummaryChange,
  isEditingSummary,
  onEditingSummaryChange,
  isSavingSummary,
  onSavingSummaryChange
}) => {
  const { notify } = useNotifications();
  // State management
  const [isEditingFileName, setIsEditingFileName] = useState(false);
  const [editedFileName, setEditedFileName] = useState('');
  const [isSavingFileName, setIsSavingFileName] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isPublic, setIsPublic] = useState(evidence?.isPublic || false);
  const [isSavingPrivacy, setIsSavingPrivacy] = useState(false);
  const menuRef = useRef(null);
  const [casefileIsPublic, setCasefileIsPublic] = useState(null);

  // Update state when evidence changes
  useEffect(() => {
    if (evidence?.fileName) {
      setEditedFileName(evidence.fileName);
    }
    if (evidence?.isPublic !== undefined) {
      setIsPublic(evidence.isPublic || false);
    }
  }, [evidence]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Load casefile privacy if casefileId present
  useEffect(() => {
    let isMounted = true;
    const loadCasefilePrivacy = async () => {
      try {
        if (!casefileId) return;
        const cfRef = doc(db, 'casefiles', casefileId);
        const snap = await getDoc(cfRef);
        if (isMounted && snap.exists()) {
          const data = snap.data();
          setCasefileIsPublic(!!data?.isPublic);
        }
      } catch (_) {
        // Leave null if fetch fails (unknown)
      }
    };
    loadCasefilePrivacy();
    return () => { isMounted = false; };
  }, [casefileId]);

  // Handle body scroll locking for modal mode
  useEffect(() => {
    if (mode === 'modal' && isOpen && !useModalLayerManager) {
      // Legacy body class management (only when not using ModalLayerManager)
      document.body.classList.add('evidence-modal-open');
      
      return () => {
        // Remove CSS class when modal closes
        document.body.classList.remove('evidence-modal-open');
      };
    }
    // When useModalLayerManager is true, body scroll is managed by ModalLayerManager
  }, [mode, isOpen, useModalLayerManager]);

  // Helper functions
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const isOwner = currentUser && currentUser.uid === evidence?.createdBy;
  
  // File type detection using shared utility
  const evidenceFileName = evidence?.fileName || evidence?.name;
  const evidenceContentType = evidence?.contentType;
  const isVideo = (evidence?.type === 'video') ||
                  (evidenceContentType && evidenceContentType.startsWith('video/')) ||
                  isVideoFile(evidenceFileName, evidenceContentType);
  const isImage = (evidence?.type === 'image') ||
                  (evidenceContentType && evidenceContentType.startsWith('image/')) ||
                  isImageFile(evidenceFileName, evidenceContentType);
  const isAudio = (evidence?.type === 'audio') ||
                  (evidenceContentType && evidenceContentType.startsWith('audio/')) ||
                  isAudioFile(evidenceFileName, evidenceContentType);

  // Video autoplay with proper React Context management
  // Generate stable unique ID for video tracking
  const videoId = React.useMemo(() => {
    return `uew-${evidence?.id || evidence?.fileName || `fallback-${Date.now()}-${Math.random()}`}`;
  }, [evidence?.id, evidence?.fileName]);
  
  const videoAutoplay = useVideoAutoplay({
    videoId,
    enabled: mode === 'tile' && isVideo,
    visibilityThreshold: 0.6,
    metadata: {
      evidenceId: evidence?.id,
      fileName: evidence?.fileName,
      mode
    }
  });

  // Destructure for easier access
  const { 
    getVideoProps, 
    getContainerProps,
    isPlaying,
    hasEnded,
    restart,
    videoRef: autoplayVideoRef
  } = videoAutoplay;

  // Simple: Show play overlay if video is paused and visible in tile mode
  // Hide it if video is playing or has ended
  const shouldShowPlayOverlay = mode === 'tile' && !isPlaying && !hasEnded;

  // Autoplay video when modal opens
  useEffect(() => {
    if (mode === 'modal' && isVideo && isOpen && autoplayVideoRef?.current) {
      // Small delay to ensure video element is ready
      const timer = setTimeout(() => {
        const video = autoplayVideoRef.current;
        if (video) {
          // Start muted for autoplay to work, then unmute after playing starts
          video.muted = true;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('✅ Video autoplay successful in modal');
                // Unmute after a brief moment so user gets sound
                setTimeout(() => {
                  if (video) {
                    video.muted = false;
                  }
                }, 100);
              })
              .catch(err => {
                console.log('ℹ️ Video autoplay blocked (user will need to click play):', err.message);
              });
          }
        }
      }, 300); // 300ms delay to ensure modal animation completes and video is ready

      return () => clearTimeout(timer);
    }
  }, [mode, isVideo, isOpen, autoplayVideoRef]);

  // Validate evidence data
  if (!evidence) {
    console.warn('UniversalEvidenceWindow: No evidence provided');
    return null;
  }

  if (!evidence.id || !evidence.fileName) {
    console.error('UniversalEvidenceWindow: Invalid evidence data', evidence);
    return (
      <EvidenceErrorBoundary 
        evidenceFileName={evidence.fileName || 'Unknown file'}
        evidenceId={evidence.id}
        mode={mode}
      >
        <div className="evidence-validation-error">
          Invalid evidence data: Missing required fields
        </div>
      </EvidenceErrorBoundary>
    );
  }

  // Safe early return after hooks and validation
  if (mode === 'modal' && !isOpen) return null;

  // Handlers
  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  const handleEditFileName = () => {
    setIsEditingFileName(true);
    setShowMenu(false);
  };


  const handleDownload = () => {
    if (evidence.downloadURL) {
      window.open(evidence.downloadURL, '_blank');
    }
    setShowMenu(false);
  };

  const handleCancelEdit = () => {
    setEditedFileName(evidence.fileName);
    setIsEditingFileName(false);
  };

  const handleSaveFileName = async () => {
    if (!editedFileName.trim() || editedFileName === evidence.fileName) {
      setIsEditingFileName(false);
      return;
    }

    // Null check to prevent crashes
    if (!casefileId || !evidence?.id) {
      console.error('Cannot save filename: missing casefileId or evidence.id');
      notify({ severity: 'error', title: 'Update Failed', message: 'Missing required information.' });
      return;
    }

    setIsSavingFileName(true);
    try {
      const evidenceRef = doc(db, 'casefiles', casefileId, 'evidence', evidence.id);
      await updateDoc(evidenceRef, {
        fileName: editedFileName.trim()
      });
      
      if (onUpdateSuccess) {
        onUpdateSuccess({ ...evidence, fileName: editedFileName.trim() });
      }
      
      setIsEditingFileName(false);
    } catch (error) {
      console.error('Error updating file name:', error);
      notify({ severity: 'error', title: 'Update Failed', message: 'Failed to update file name.' });
    } finally {
      setIsSavingFileName(false);
    }
  };

  const handleTogglePrivacy = async () => {
    if (!isOwner) {
      console.log('Only evidence owner can change privacy settings');
      return;
    }

    // Null check to prevent crashes
    if (!casefileId || !evidence?.id) {
      console.error('Cannot toggle privacy: missing casefileId or evidence.id');
      notify({ severity: 'error', title: 'Privacy Update Failed', message: 'Missing required information.' });
      return;
    }

    setIsSavingPrivacy(true);
    try {
      const newIsPublic = !isPublic;
      const evidenceRef = doc(db, 'casefiles', casefileId, 'evidence', evidence.id);
      await updateDoc(evidenceRef, {
        isPublic: newIsPublic
      });
      
      setIsPublic(newIsPublic);
      
      if (onUpdateSuccess) {
        onUpdateSuccess({ ...evidence, isPublic: newIsPublic });
      }
    } catch (error) {
      console.error('Error updating privacy setting:', error);
      notify({ severity: 'error', title: 'Privacy Update Failed', message: 'Please try again.' });
    } finally {
      setIsSavingPrivacy(false);
    }
  };

  // Determine which components to attach based on mode
  const componentsToAttach = mode === 'modal' ? 
    (
      disableAutoAIAnalysis
        ? attachedComponents
        : [...attachedComponents, ...(attachedComponents.find(c => c === AIAnalysisComponent || (typeof c === 'function')) ? [] : [
            (props) => (
              <AIAnalysisComponent
                {...props}
                initialTab={desiredAnalysisTab}
                navigationTrigger={navigationTrigger}
              />
            )
          ])]
    )
    : attachedComponents;

  // Container class based on mode
  const containerClass = `uew-container ${mode} ${isSelected ? 'selected' : ''} ${className}`;

  // Render the appropriate wrapper based on mode
  if (mode === 'modal') {
    return (
      <EvidenceErrorBoundary 
        evidenceFileName={evidence.fileName}
        evidenceId={evidence.id}
        mode={mode}
      >
        <div 
          className={`uew-modal-backdrop ${isOpen ? 'open' : ''}`} 
          style={zIndex ? { zIndex } : {}}
          data-modal-id={modalId}
          onClick={onClose}
        >
          <div className={`uew-modal-wrapper ${isOpen ? 'open' : ''}`} onClick={e => e.stopPropagation()}>
            <button className="uew-modal-close" onClick={onClose}>×</button>
            {renderContent()}
          </div>
        </div>
      </EvidenceErrorBoundary>
    );
  }

  // For tile and compact modes
  return (
    <EvidenceErrorBoundary 
      evidenceFileName={evidence.fileName}
      evidenceId={evidence.id}
      mode={mode}
    >
      {renderContent()}
    </EvidenceErrorBoundary>
  );

  function renderContent() {
    return (
      <div 
        className={containerClass}
        onClick={() => mode === 'tile' && onOpenModal && onOpenModal(evidence)}
        onKeyDown={(e) => {
          if (mode === 'tile' && onOpenModal && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onOpenModal(evidence);
          }
        }}
        tabIndex={mode === 'tile' && onOpenModal ? 0 : -1}
        role={mode === 'tile' && onOpenModal ? 'button' : 'article'}
        aria-label={mode === 'tile' && onOpenModal ? `Open evidence: ${evidence.fileName}` : `Evidence: ${evidence.fileName}`}
        {...getContainerProps()}
      >
        {/* Header */}
        <div className="uew-header">
          <div className="uew-header-top">
            <div className="uew-casefile-location">
              <span className="uew-path-root">Case</span>/
              <span className={`uew-tag ${casefileIsPublic ? 'public' : 'private'}`}>
                {casefileIsPublic ? 'Public' : 'Private'}
              </span>/
              <span className="uew-name">{casefileIsPublic ? (casefileName || 'casefile') : '...'}</span>
            </div>
            {mode === 'modal' && (
              <div className="uew-header-actions">
                <div className="uew-menu" ref={menuRef}>
                  <button 
                    className="uew-menu-btn" 
                    onClick={handleMenuClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && showMenu) {
                        e.preventDefault();
                        setShowMenu(false);
                      }
                    }}
                    aria-label="Evidence options menu"
                    aria-expanded={showMenu}
                    aria-haspopup="menu"
                    type="button"
                  >
                    <FaEllipsisV aria-hidden="true" />
                  </button>
                  {showMenu && (
                    <div 
                      className="uew-dropdown-menu"
                      role="menu"
                      aria-label="Evidence options"
                    >
                      <button 
                        onClick={handleEditFileName} 
                        className="menu-item"
                        role="menuitem"
                        type="button"
                      >
                        <FaEdit aria-hidden="true" /> Edit Title
                      </button>
                      <div className="menu-divider"></div>
                      <button 
                        onClick={handleDownload} 
                        className="menu-item"
                        role="menuitem"
                        type="button"
                      >
                        <FaDownload aria-hidden="true" /> Download
                      </button>
                      {/* Privacy toggle moved into the menu when owner */}
                      {isOwner && (
                        <>
                          <div className="menu-divider"></div>
                          <button 
                            onClick={handleTogglePrivacy} 
                            className="menu-item" 
                            disabled={isSavingPrivacy}
                            role="menuitem"
                            type="button"
                            aria-label={isSavingPrivacy ? 'Saving privacy setting...' : (isPublic ? 'Make evidence private' : 'Make evidence public')}
                          >
                            {isSavingPrivacy ? 'Saving…' : (isPublic ? 'Make Private' : 'Make Public')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="uew-header-divider"></div>
          <div className="uew-header-bottom">
            {isEditingFileName && mode === 'modal' ? (
              <div className="uew-inline-edit">
                <input
                  type="text"
                  value={editedFileName}
                  onChange={(e) => setEditedFileName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveFileName();
                    else if (e.key === 'Escape') handleCancelEdit();
                  }}
                  className="uew-edit-input"
                  autoFocus
                  maxLength={255}
                />
                <button onClick={handleSaveFileName} disabled={isSavingFileName}>
                  {isSavingFileName ? '...' : '✓'}
                </button>
                <button onClick={handleCancelEdit} disabled={isSavingFileName}>
                  ✕
                </button>
              </div>
            ) : (
              <>
                <span className="uew-path-root">Incident</span>/
                <span className={`uew-tag ${isPublic ? 'public' : 'private'}`}>{isPublic ? 'Public' : 'Private'}</span>/
                <h3 className="uew-title">{isPublic ? (evidence.fileName || 'Untitled Evidence') : '...'}</h3>
                <span className="uew-date">• Uploaded {formatDate(evidence.createdAt)}</span>
                {/* Analysis Status Badges - Investigation Feature (both tile and modal for context) */}
                {evidence.analysisCount > 0 && (
                  <span className="uew-analysis-badge" title={`${evidence.analysisCount} analysis ${evidence.analysisCount === 1 ? 'result' : 'results'} available`}>
                    <span className="uew-badge-icon">🔍</span>
                    <span className="uew-badge-count">{evidence.analysisCount}</span>
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Privacy toggle moved to menu to simplify layout in modal */}

        {/* Media Content */}
        <div className="uew-content">
          {isVideo && evidence.downloadURL && (
            <div className="uew-video">
              <video 
                src={evidence.downloadURL} 
                controls={true} // Always show native browser controls for full functionality
                poster={evidence.thumbnailURL || ''} // Use thumbnail if available
                preload="metadata" // Always load metadata for autoplay to work
                onClick={(e) => {
                  // Stop propagation to prevent modal from opening when clicking video
                  e.stopPropagation();
                }}
                onError={(e) => {
                  console.error('Video load error:', e);
                  e.target.style.display = 'none';
                }}
                {...getVideoProps()}
              />
              {/* "Tap to play" overlay - show when video is paused */}
              {shouldShowPlayOverlay && (
                <div 
                  className="uew-video-tap-overlay"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('User tapped play overlay');
                    // Manually trigger play
                    if (autoplayVideoRef?.current) {
                      const playPromise = autoplayVideoRef.current.play();
                      if (playPromise) {
                        playPromise
                          .then(() => {
                            console.log('Manual play successful');
                          })
                          .catch(err => {
                            console.warn('Manual play failed:', err);
                          });
                      }
                    }
                  }}
                >
                  <div className="uew-video-tap-icon">▶</div>
                  <div className="uew-video-tap-text">Tap to play</div>
                </div>
              )}
              
              {/* Replay overlay when video ends */}
              {hasEnded && (
                <div 
                  className="uew-video-replay-overlay"
                  onClick={(e) => {
                    e.stopPropagation();
                    restart();
                  }}
                >
                  <div className="uew-video-replay-icon">↻</div>
                  <div className="uew-video-replay-text">Replay</div>
                </div>
              )}
            </div>
          )}
          
          {isImage && evidence.downloadURL && (
            <div className="uew-image">
              <img 
                src={evidence.downloadURL} 
                alt={`Evidence image: ${evidence.fileName}`}
                className="uew-media"
                loading="lazy"
                onError={(e) => {
                  console.error('Image load error:', e);
                  e.target.style.display = 'none';
                  e.target.setAttribute('aria-label', `Failed to load image: ${evidence.fileName}`);
                }}
              />
            </div>
          )}
          
          {isAudio && (
            <div className="uew-audio">
              {mode === 'modal' && evidence.downloadURL ? (
                <audio 
                  src={evidence.downloadURL} 
                  controls 
                  className="uew-media"
                />
              ) : (
                <div className="uew-audio-placeholder">
                  <div className="uew-audio-icon">🎵</div>
                  <div className="uew-audio-info">
                    <p>Audio File</p>
                    {formatFileSize && evidence.fileSize && (
                      <span className="uew-file-size">{formatFileSize(evidence.fileSize)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {!isVideo && !isImage && !isAudio && (
            <div className="uew-file">
              <div className="uew-file-placeholder">
                <div className="uew-file-icon">📄</div>
                <div className="uew-file-info">
                  <p>File</p>
                  {formatFileSize && evidence.fileSize && (
                    <span className="uew-file-size">{formatFileSize(evidence.fileSize)}</span>
                  )}
                  {mode === 'modal' && evidence.downloadURL && (
                    <a 
                      href={evidence.downloadURL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="uew-download-link"
                    >
                      Download File
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {actionButtons.length > 0 && (
          <div className="uew-actions">
            {actionButtons.map((button) => (
              <button 
                key={button.key || button.label}
                className={`uew-action-btn ${button.variant || 'primary'}`}
                onClick={button.onClick}
                disabled={button.disabled}
              >
                {button.icon} {button.label}
              </button>
            ))}
            
          </div>
        )}

        {/* Summary removed - now only appears in Evidence Summary box in Info tab */}

        {/* Attached Components (modal mode) */}
        {mode === 'modal' && componentsToAttach.map((Component, index) => (
          <div key={index} className="uew-attached-component">
            <Component 
              evidence={evidence}
              casefileId={casefileId}
              casefileName={casefileName}
              currentUser={currentUser}
              onUpdateSuccess={onUpdateSuccess}
              evidenceSummary={evidenceSummary}
              onSummaryChange={onSummaryChange}
              isEditingSummary={isEditingSummary}
              onEditingSummaryChange={onEditingSummaryChange}
              isSavingSummary={isSavingSummary}
              onSavingSummaryChange={onSavingSummaryChange}
            />
          </div>
        ))}
      </div>
    );
  }
};

// PropTypes for type validation and documentation
UniversalEvidenceWindow.propTypes = {
  // Core props
  evidence: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    downloadURL: PropTypes.string.isRequired,
    contentType: PropTypes.string,
    type: PropTypes.oneOf(['video', 'image', 'audio', 'document']),
    thumbnailURL: PropTypes.string,
    isPublic: PropTypes.bool,
    createdBy: PropTypes.string,
    createdAt: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object // Firestore Timestamp
    ]),
    summary: PropTypes.string,
    fileSize: PropTypes.number
  }).isRequired,
  
  // Mode configuration
  mode: PropTypes.oneOf(['tile', 'modal', 'compact']),
  isOpen: PropTypes.bool,
  
  // Event handlers
  onClose: PropTypes.func,
  onOpenModal: PropTypes.func,
  onUpdateSuccess: PropTypes.func,
  onSelectionChange: PropTypes.func,
  
  // User and context
  currentUser: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    email: PropTypes.string,
    displayName: PropTypes.string
  }),
  casefileId: PropTypes.string,
  casefileName: PropTypes.string,
  
  // UI configuration
  actionButtons: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.element,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'scan', 'results', 'bounty']),
    disabled: PropTypes.bool
  })),
  attachedComponents: PropTypes.arrayOf(PropTypes.func),
  disableAutoAIAnalysis: PropTypes.bool,
  isSelected: PropTypes.bool,
  formatFileSize: PropTypes.func,
  className: PropTypes.string,
  
  // Summary state management (controlled by parent)
  evidenceSummary: PropTypes.string,
  onSummaryChange: PropTypes.func,
  isEditingSummary: PropTypes.bool,
  onEditingSummaryChange: PropTypes.func,
  isSavingSummary: PropTypes.bool,
  onSavingSummaryChange: PropTypes.func
};

// Default props for optional values
UniversalEvidenceWindow.defaultProps = {
  mode: 'tile',
  isOpen: true,
  actionButtons: [],
  attachedComponents: [],
  disableAutoAIAnalysis: false,
  isSelected: false,
  className: '',
  evidenceSummary: '',
  isEditingSummary: false,
  isSavingSummary: false
};

export default UniversalEvidenceWindow;
